import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { Trophy, Medal, Search } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

const MEDALS = { 1: "text-gold-300", 2: "text-slate-300", 3: "text-amber-600" };

function Podium({ rows }) {
  const top = [rows[1], rows[0], rows[2]];
  const order = [2, 1, 3];
  return (
    <div className="grid grid-cols-3 gap-3 items-end mb-8 max-w-2xl mx-auto">
      {top.map((r, i) => {
        const pos = order[i];
        if (!r) return <div key={i} />;
        const h = pos === 1 ? "h-36" : pos === 2 ? "h-28" : "h-24";
        return (
          <div key={r.user_id} className="flex flex-col items-center" data-testid={`podium-${pos}`}>
            <Medal className={MEDALS[pos]} size={pos === 1 ? 34 : 26} />
            <div className="font-semibold text-white text-sm mt-1 truncate max-w-full text-center">{r.nickname}</div>
            <div className="font-heading text-2xl font-black gold-gradient-text">{r.points}</div>
            <div className={`w-full ${h} surface-elevated rounded-t-lg mt-2 grid place-items-center border-b-0 ${pos === 1 ? "border-gold-300/50 shadow-goldsm" : ""}`}>
              <span className="font-heading text-4xl font-black text-ink-600">{pos}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useApp();
  const [scope, setScope] = useState("period");
  const [data, setData] = useState({ rows: [], period: null });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/leaderboard", { params: { scope } }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [scope]);

  const rows = (data.rows || []).filter((r) => r.nickname.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader overline={scope === "period" ? data.period?.name || "Periodo corrente" : "Tutti i tempi"} title="Classifica"
        subtitle="La classifica si aggiorna automaticamente dopo la conferma dei risultati ufficiali." />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row gap-3 justify-between mb-6">
          <div className="flex gap-2">
            <button data-testid="scope-period" onClick={() => setScope("period")} className={`px-4 py-2 rounded-full text-sm font-medium border transition ${scope === "period" ? "bg-gold-300 text-ink-900 border-gold-300" : "border-ink-600 text-slate-300"}`}>Mensile (30 giorni)</button>
            <button data-testid="scope-historical" onClick={() => setScope("historical")} className={`px-4 py-2 rounded-full text-sm font-medium border transition ${scope === "historical" ? "bg-gold-300 text-ink-900 border-gold-300" : "border-ink-600 text-slate-300"}`}>Storica</button>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="leaderboard-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nickname…" className="input-dark !pl-9 !py-2" />
          </div>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-16">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="surface p-10 text-center text-muted-foreground">Ancora nessun punteggio in classifica. Sii il primo a pronosticare!</div>
        ) : (
          <>
            {!q && rows.length >= 1 && <Podium rows={rows} />}
            <div className="surface overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground border-b border-gold-300/15 font-mono-ui">
                <div className="col-span-2 sm:col-span-1">#</div>
                <div className="col-span-5 sm:col-span-5">Nickname</div>
                <div className="col-span-2 text-center hidden sm:block">Giocate</div>
                <div className="col-span-2 text-center">Corretti</div>
                <div className="col-span-3 sm:col-span-1 text-center hidden sm:block">%</div>
                <div className="col-span-3 sm:col-span-1 text-right">Punti</div>
              </div>
              {rows.map((r) => {
                const mine = user && r.user_id === user.id;
                return (
                  <div key={r.user_id} data-testid={`leaderboard-row-${r.position}`}
                    className={`grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm border-b border-gold-300/5 ${mine ? "bg-gold-300/10" : ""}`}>
                    <div className="col-span-2 sm:col-span-1 font-heading font-bold text-lg flex items-center gap-1">
                      {r.position <= 3 ? <Medal size={18} className={MEDALS[r.position]} /> : <span className="text-muted-foreground">{r.position}</span>}
                    </div>
                    <div className="col-span-5 sm:col-span-5 font-semibold text-white truncate">{r.nickname}{mine && <span className="text-gold-300 text-xs ml-1">(tu)</span>}</div>
                    <div className="col-span-2 text-center text-muted-foreground hidden sm:block">{r.predicted}</div>
                    <div className="col-span-2 text-center text-status-correct">{r.correct}</div>
                    <div className="col-span-3 sm:col-span-1 text-center text-muted-foreground hidden sm:block">{r.success_rate}%</div>
                    <div className="col-span-3 sm:col-span-1 text-right font-heading font-black text-lg text-gold-300">{r.points}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
